/**
 * Rotatividade (turnover).
 *
 * A tela mostra **duas** taxas, e não uma, porque elas contam histórias opostas: uma empresa que
 * contratou trinta e demitiu dois tem rotatividade geral alta e rotatividade de desligamento
 * baixa. Mostrar só a primeira faria crescimento parecer problema.
 *
 * E mostra as **parcelas** — admissões, desligamentos, efetivo inicial e final. O número que vai
 * para o cliente precisa ser conferível; taxa sozinha ninguém audita. Os gráficos dão a leitura
 * rápida (onde entrou/saiu gente, para onde a taxa foi no ano); a tabela mantém o detalhe.
 *
 * ## Por que existe um destaque no topo
 *
 * A primeira versão punha os onze números em onze quadradinhos iguais, na mesma cor e no mesmo
 * corpo de letra. Quem abria a tela via uma parede cinza e tinha de LER os rótulos para descobrir
 * qual era a resposta da pergunta que trouxe a pessoa ali — "girou muito?". Um quadro de
 * indicadores sem hierarquia não é sóbrio, é mudo.
 *
 * Então o número da pergunta subiu para um bloco próprio, grande e colorido pela faixa em que
 * cai, com a variação contra o ano anterior ao lado e a frase que o torna conferível ("X entraram
 * e Y saíram de um efetivo médio de Z"). Os outros números continuam todos ali embaixo, agrupados
 * pelo que respondem. Nada foi escondido: foi ordenado.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  TrendingDown as SaidaIcon,
  TrendingUp as EntradaIcon,
  Groups as EfetivoIcon,
  ArrowUpward as SubiuIcon,
  ArrowDownward as CaiuIcon,
  BarChart as BarraIcon,
  ShowChart as LinhaIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { PageShell, DataTable, StatCard, SectionCard, type DataTableColumn } from '../../components';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { companyService } from '../../services/companyService';
import turnoverService from '../../services/turnoverService';
import useCompanyContext from '../../hooks/useCompanyContext';
import {
  emPorcento,
  faixaDaRotatividade,
  rotuloDoMes,
  tempoLegivel,
  type FaixaDaRotatividade,
  type MesDoTurnover,
  type TurnoverDoPeriodo,
  type TempoDeCasa,
  type SetorDetalhado,
  type DesligamentosPorNatureza,
  type ChurnDeExperiencia,
} from '../../types/turnover';

interface EmpresaDaLista {
  id: number;
  name: string;
}

const colunas: DataTableColumn<MesDoTurnover>[] = [
  { id: 'mes', label: 'Mês', primary: true, render: (m) => rotuloDoMes(m.mes) },
  { id: 'admissoes', label: 'Admissões', render: (m) => String(m.admissoes) },
  { id: 'desligamentos', label: 'Desligamentos', render: (m) => String(m.desligamentos) },
  {
    id: 'efetivoMedio',
    label: 'Efetivo médio',
    render: (m) => m.efetivoMedio.toLocaleString('pt-BR'),
  },
  {
    id: 'turnoverGeral',
    label: 'Rotatividade geral',
    render: (m) => emPorcento(m.turnoverGeral),
  },
  {
    id: 'turnoverDesligamento',
    label: 'Só desligamentos',
    render: (m) => emPorcento(m.turnoverDesligamento),
  },
];

/** O ano corrente pelo calendário local — `getFullYear`, nunca `toISOString`. */
function anoAtual(): number {
  return new Date().getFullYear();
}

/** Rótulo pequeno em caixa alta — o mesmo em todas as divisões da tela. */
const RotuloDeSecao: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{ display: 'block', letterSpacing: 1, mb: 1 }}
  >
    {children}
  </Typography>
);

/** Número de apoio dentro do destaque: menor que o principal, maior que um rótulo. */
const NumeroDeApoio: React.FC<{ rotulo: string; valor: string; sub?: string }> = ({
  rotulo,
  valor,
  sub,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block" noWrap>
      {rotulo}
    </Typography>
    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }} noWrap>
      {valor}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary" noWrap>
        {sub}
      </Typography>
    )}
  </Box>
);

export const TurnoverPage: React.FC = () => {
  const theme = useTheme();
  const { shouldShowCompanyField, effectiveCompanyId } = useCompanyContext();

  const [empresaId, setEmpresaId] = useState<string>(
    effectiveCompanyId ? String(effectiveCompanyId) : ''
  );
  const [ano, setAno] = useState(anoAtual());
  const [meses, setMeses] = useState<MesDoTurnover[]>([]);
  const [doAno, setDoAno] = useState<TurnoverDoPeriodo | null>(null);
  const [tempo, setTempo] = useState<TempoDeCasa | null>(null);
  const [setores, setSetores] = useState<SetorDetalhado[]>([]);
  const [natureza, setNatureza] = useState<DesligamentosPorNatureza | null>(null);
  const [churn, setChurn] = useState<ChurnDeExperiencia | null>(null);
  const [anoAnterior, setAnoAnterior] = useState<TurnoverDoPeriodo | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregarEmpresas = useCallback(
    async (): Promise<readonly EmpresaDaLista[]> => companyService.getCompaniesForDropdown(),
    []
  );
  const empresas = useListaPermitida<EmpresaDaLista>(
    'company.read',
    'Empresas',
    carregarEmpresas
  );

  const carregar = useCallback(async () => {
    if (!empresaId) return;
    setCarregando(true);
    setErro(null);
    try {
      const [mensal, periodo, tempoDeCasa, setoresDetalhados, porNatureza, churnExp, anterior] = await Promise.all([
        turnoverService.porMes(empresaId, ano),
        turnoverService.doPeriodo(empresaId, `${ano}-01-01`, `${ano}-12-31`),
        turnoverService.tempoDeCasa(empresaId),
        turnoverService.porSetorDetalhado(empresaId, `${ano}-01-01`, `${ano}-12-31`),
        turnoverService.porNatureza(empresaId, `${ano}-01-01`, `${ano}-12-31`),
        turnoverService.churnDeExperiencia(empresaId, `${ano}-01-01`, `${ano}-12-31`),
        turnoverService.doPeriodo(empresaId, `${ano - 1}-01-01`, `${ano - 1}-12-31`),
      ]);
      setMeses(mensal);
      setDoAno(periodo);
      setTempo(tempoDeCasa);
      setSetores(setoresDetalhados);
      setNatureza(porNatureza);
      setChurn(churnExp);
      setAnoAnterior(anterior);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível calcular a rotatividade');
    } finally {
      setCarregando(false);
    }
  }, [empresaId, ano]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const anos = useMemo(() => {
    const atual = anoAtual();
    return [atual, atual - 1, atual - 2, atual - 3, atual - 4];
  }, []);

  // Cores dos gráficos vêm do tema (funcionam em claro e escuro).
  const cores = useMemo(
    () => ({
      admissao: theme.palette.success.main,
      desligamento: theme.palette.error.main,
      geral: theme.palette.primary.main,
      soDesligamento: theme.palette.warning.main,
      grade: theme.palette.divider,
      eixo: theme.palette.text.secondary,
    }),
    [theme]
  );

  /** A cor de uma taxa segue a faixa em que ela cai — a mesma regra do quadro e da tabela. */
  const corDaFaixa = useCallback(
    (faixa: FaixaDaRotatividade): string =>
      faixa === 'alta'
        ? theme.palette.error.main
        : faixa === 'atencao'
          ? theme.palette.warning.main
          : theme.palette.success.main,
    [theme]
  );

  /**
   * Caixa do gráfico pintada pelo tema: o balão padrão do Recharts é branco fixo, e no modo
   * escuro virava um retângulo claro com texto claro dentro — ilegível justamente na hora de ler.
   */
  const balao = useMemo(
    () => ({
      contentStyle: {
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        boxShadow: theme.shadows[3],
        fontSize: 12,
      },
      labelStyle: { color: theme.palette.text.primary, fontWeight: 600 },
      itemStyle: { color: theme.palette.text.secondary },
    }),
    [theme]
  );

  const dadosGrafico = useMemo(
    () =>
      meses.map((m) => ({
        rotulo: rotuloDoMes(m.mes),
        Admissões: m.admissoes,
        Desligamentos: m.desligamentos,
        'Rotatividade geral': Math.round(m.turnoverGeral * 100) / 100,
        'Só desligamentos': Math.round(m.turnoverDesligamento * 100) / 100,
      })),
    [meses]
  );

  const temDados = dadosGrafico.length > 0;

  /**
   * A tabela por setor ganha uma barra proporcional ao lado do número: vinte linhas de
   * porcentagem em texto obrigam a comparar de cabeça, e a barra responde "qual é o pior setor"
   * antes de se ler qualquer número. A escala é relativa ao pior setor da lista, e não a 100%,
   * senão numa empresa saudável todas as barras ficam com um fiapo e a comparação se perde.
   */
  const colunasSetor: DataTableColumn<SetorDetalhado>[] = useMemo(() => {
    const pior = Math.max(...setores.map((s) => s.taxa), 1);
    return [
      { id: 'nome', label: 'Setor', primary: true, render: (s) => s.nome },
      { id: 'efetivo', label: 'Efetivo', align: 'right', render: (s) => s.efetivo },
      { id: 'desligamentos', label: 'Saídas', align: 'right', render: (s) => s.desligamentos },
      {
        id: 'taxa',
        label: 'Rotatividade',
        render: (s) => {
          const cor = corDaFaixa(faixaDaRotatividade(s.taxa));
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 170 }}>
              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: alpha(cor, 0.16),
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${String(Math.round((s.taxa / pior) * 100))}%`,
                    height: '100%',
                    borderRadius: 3,
                    bgcolor: cor,
                  }}
                />
              </Box>
              <Typography variant="body2" fontWeight={700} sx={{ minWidth: 60, textAlign: 'right' }}>
                {emPorcento(s.taxa)}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: 'tempoMedioDias',
        label: 'Tempo médio de casa',
        align: 'right',
        render: (s) => tempoLegivel(s.tempoMedioDias),
      },
    ];
  }, [setores, corDaFaixa]);

  const faixa = doAno ? faixaDaRotatividade(doAno.turnoverGeral) : 'normal';
  const destaque = corDaFaixa(faixa);

  /**
   * A comparação com o ano anterior só aparece quando existiu ano anterior. Sem esta guarda, uma
   * empresa cadastrada este ano abria a tela anunciando "+0,0 pp, ano anterior: 0,00%" — uma
   * comparação contra o nada, que parece estabilidade e é ausência de dado.
   */
  const variacao =
    doAno && anoAnterior && anoAnterior.efetivoMedio > 0
      ? doAno.turnoverGeral - anoAnterior.turnoverGeral
      : null;

  const saldo = doAno ? doAno.admissoes - doAno.desligamentos : 0;

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Rotatividade' }]}
      title="Rotatividade"
      subtitle="Quanta gente entrou e saiu, e quanto isso representa do efetivo"
      actions={
        empresaId && temDados ? (
          <Button
            variant="contained"
            onClick={() => window.open(`/rotatividade/apresentacao/${empresaId}?ano=${ano}`, '_blank', 'noopener')}
          >
            Gerar apresentação
          </Button>
        ) : undefined
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {shouldShowCompanyField && (
          <TextField
            select
            label="Empresa"
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
            disabled={!empresas.permitido}
            helperText={empresas.bloqueio ?? undefined}
            sx={{ minWidth: 280 }}
          >
            {empresas.itens.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label="Ano"
          value={String(ano)}
          onChange={(e) => setAno(Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          {anos.map((a) => (
            <MenuItem key={a} value={String(a)}>
              {a}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {doAno && doAno.inconsistencias > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {doAno.inconsistencias} funcionário(s) marcados como demitidos <b>sem data de
          desligamento</b>. Eles não entram em nenhum período, então a rotatividade abaixo está
          menor do que a real. Corrija a data no cadastro.
        </Alert>
      )}

      {doAno && (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2.5, md: 3 },
            mb: 3,
            borderRadius: 2,
            borderTop: `4px solid ${destaque}`,
            background: `linear-gradient(135deg, ${alpha(destaque, 0.07)} 0%, ${alpha(destaque, 0)} 55%)`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 1fr) 1.2fr' },
              gap: { xs: 3, md: 4 },
              alignItems: 'center',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <RotuloDeSecao>Rotatividade em {ano}</RotuloDeSecao>

              <Stack direction="row" alignItems="baseline" gap={1.5} flexWrap="wrap">
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: 40, md: 52 },
                    fontWeight: 800,
                    lineHeight: 1,
                    color: destaque,
                  }}
                >
                  {emPorcento(doAno.turnoverGeral)}
                </Typography>
                {variacao !== null && anoAnterior && (
                  <Chip
                    size="small"
                    icon={variacao >= 0 ? <SubiuIcon /> : <CaiuIcon />}
                    color={variacao >= 0 ? 'error' : 'success'}
                    variant="outlined"
                    label={`${variacao >= 0 ? '+' : ''}${variacao.toFixed(1)} pp vs ${ano - 1} (${emPorcento(anoAnterior.turnoverGeral)})`}
                  />
                )}
              </Stack>

              {/* A frase que torna a taxa conferível: quem só vê "6,93%" não tem como auditar. */}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {doAno.admissoes} entraram e {doAno.desligamentos} saíram, de um efetivo médio de{' '}
                {doAno.efetivoMedio.toLocaleString('pt-BR')} — de {doAno.efetivoInicial} a{' '}
                {doAno.efetivoFinal} pessoas no ano.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <NumeroDeApoio
                  rotulo="Só desligamentos"
                  valor={emPorcento(doAno.turnoverDesligamento)}
                  sub="perda de gente"
                />
                <NumeroDeApoio
                  rotulo="Saldo no ano"
                  valor={`${saldo > 0 ? '+' : ''}${saldo}`}
                  sub="admissões − saídas"
                />
                {tempo && (
                  <NumeroDeApoio
                    rotulo="Tempo médio de casa"
                    valor={tempoLegivel(tempo.mediaGeralDias)}
                    sub={`quem saiu: ${tempoLegivel(tempo.mediaDesligadosDias)}`}
                  />
                )}
              </Stack>
            </Box>

            {/* O ano inteiro em miniatura, ao lado do número do ano: a forma da curva diz se o
                giro foi constante ou se veio de um mês só, que é a diferença entre rotina e
                evento — e nenhum quadradinho de indicador consegue dizer isso. */}
            {temDados && (
              <Box sx={{ height: { xs: 130, md: 168 }, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dadosGrafico} margin={{ left: 0, right: 4, top: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="destaqueDoAno" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={destaque} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={destaque} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="rotulo"
                      tick={{ fill: cores.eixo, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                      minTickGap={16}
                    />
                    <RechartsTooltip
                      {...balao}
                      formatter={(value) => `${Number(value).toLocaleString('pt-BR')}%`}
                    />
                    <Area
                      type="monotone"
                      dataKey="Rotatividade geral"
                      stroke={destaque}
                      strokeWidth={2}
                      fill="url(#destaqueDoAno)"
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {doAno && (
        <>
          <RotuloDeSecao>Onde a rotatividade se concentra</RotuloDeSecao>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            <StatCard
              icon={<EntradaIcon />}
              label="Admissões no ano"
              value={String(doAno.admissoes)}
              sublabel={`${doAno.desligamentos} desligamento(s)`}
              color={cores.admissao}
            />
            <StatCard
              icon={<EfetivoIcon />}
              label="Efetivo médio"
              value={doAno.efetivoMedio.toLocaleString('pt-BR')}
              sublabel={`de ${doAno.efetivoInicial} a ${doAno.efetivoFinal}`}
            />
            {setores.length > 0 && setores[0].desligamentos > 0 && (
              <StatCard
                icon={<SaidaIcon />}
                label="Setor com mais saídas"
                value={setores[0].nome}
                sublabel={`${setores[0].desligamentos} saída(s) · ${emPorcento(setores[0].taxa)}`}
                color={corDaFaixa(faixaDaRotatividade(setores[0].taxa))}
              />
            )}
            {churn && churn.totalSaidas > 0 && (
              <StatCard
                icon={<SaidaIcon />}
                label="Churn de experiência"
                value={emPorcento(churn.percentual)}
                sublabel={`${churn.saidasAte90d} saída(s) com < 90 dias de casa`}
                color={corDaFaixa(faixaDaRotatividade(churn.percentual))}
              />
            )}
            {natureza && doAno.efetivoMedio > 0 && (
              <StatCard
                icon={<SaidaIcon />}
                label="Turnover voluntário"
                value={emPorcento(
                  Math.round((natureza.voluntario / doAno.efetivoMedio) * 100 * 100) / 100
                )}
                sublabel="quem pediu pra sair"
              />
            )}
            {natureza && natureza.total > 0 && (
              <StatCard
                icon={<SaidaIcon />}
                label="Voluntário × involuntário"
                value={`${natureza.voluntario} × ${natureza.involuntario}`}
                sublabel={
                  natureza.naoInformado > 0
                    ? `${natureza.naoInformado} sem tipo informado`
                    : `de ${natureza.total} saída(s)`
                }
                color={natureza.naoInformado > 0 ? theme.palette.warning.main : undefined}
              />
            )}
          </Box>
        </>
      )}

      {temDados && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          <SectionCard
            title="Entradas e saídas por mês"
            icon={<BarraIcon />}
            accentColor={cores.admissao}
          >
            <Typography variant="caption" color="text.secondary">
              Admissões contra desligamentos, mês a mês.
            </Typography>
            <Box sx={{ height: 300, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dadosGrafico} margin={{ left: -8, right: 8, top: 8 }}>
                  <CartesianGrid vertical={false} stroke={cores.grade} />
                  <XAxis
                    dataKey="rotulo"
                    tick={{ fill: cores.eixo, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: cores.eixo, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <RechartsTooltip {...balao} cursor={{ fill: cores.grade, opacity: 0.3 }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Bar
                    dataKey="Admissões"
                    fill={cores.admissao}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={22}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="Desligamentos"
                    fill={cores.desligamento}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={22}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>

          <SectionCard
            title="Rotatividade ao longo do ano"
            icon={<LinhaIcon />}
            accentColor={cores.geral}
          >
            <Typography variant="caption" color="text.secondary">
              As duas taxas (%): a geral e a de só desligamentos.
            </Typography>
            <Box sx={{ height: 300, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dadosGrafico} margin={{ left: -8, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="areaGeral" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={cores.geral} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={cores.geral} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="areaDesligamento" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={cores.soDesligamento} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={cores.soDesligamento} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke={cores.grade} />
                  <XAxis
                    dataKey="rotulo"
                    tick={{ fill: cores.eixo, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    unit="%"
                    tick={{ fill: cores.eixo, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <RechartsTooltip
                    {...balao}
                    formatter={(value) => `${Number(value).toLocaleString('pt-BR')}%`}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Area
                    type="monotone"
                    dataKey="Rotatividade geral"
                    stroke={cores.geral}
                    strokeWidth={2}
                    fill="url(#areaGeral)"
                    dot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="Só desligamentos"
                    stroke={cores.soDesligamento}
                    strokeWidth={2}
                    fill="url(#areaDesligamento)"
                    dot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Box>
      )}

      {setores.length > 0 && (
        <>
          <RotuloDeSecao>Rotatividade e tempo de casa por setor ({ano})</RotuloDeSecao>
          <DataTable
            columns={colunasSetor}
            rows={setores}
            getRowId={(s) => s.departmentId ?? 'sem-setor'}
            loading={carregando}
            emptyState={{
              title: 'Nenhum setor com dados',
              description: 'Cadastre setores e vincule os funcionários para ver a rotatividade por área.',
            }}
          />
        </>
      )}

      <Box sx={{ mt: 3 }}>
        <RotuloDeSecao>Mês a mês</RotuloDeSecao>
        <DataTable
          columns={colunas}
          rows={meses}
          getRowId={(m) => m.mes}
          loading={carregando}
          emptyState={{
            title: 'Escolha a empresa e o ano',
            description: `A rotatividade é calculada pelas datas de admissão e de desligamento dos
              funcionários desta empresa — não pela situação atual, que diria a mesma coisa para
              todos os meses.`,
          }}
        />
      </Box>
    </PageShell>
  );
};

export default TurnoverPage;
